import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { notesApi, capabilitiesApi } from '@/lib/api';
import { useNotesStore } from '@/stores/notesStore';
import { useWorkspaceStore, selectActiveWorkspace } from '@/stores/workspaceStore';
import { useUIStore } from '@/stores/uiStore';
import AppShell from '@/components/layout/AppShell';
import NoteEditor from '@/components/notes/NoteEditor';
import type { Capability } from '@/types';

type SaveState = 'idle' | 'dirty' | 'saving' | 'saved';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const titleOf = (title: string) => (title.trim().length === 0 ? 'Untitled' : title);

function useQueryParam(name: string): string | null {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search).get(name), [search, name]);
}

// ─── Note list item ───────────────────────────────────────────────────────────

const NoteRow: React.FC<{
  id: string;
  title: string;
  tag?: string;
  updatedAt: string;
  active: boolean;
  onClick: () => void;
}> = ({ title, tag, updatedAt, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full text-left transition-colors relative px-3 py-2.5"
    style={{
      background: active ? 'var(--crimson-dim)' : 'transparent',
      borderLeft: `2px solid ${active ? 'var(--crimson)' : 'transparent'}`,
      color: 'var(--text)',
    }}
    onMouseOver={(e) => {
      if (active) return;
      (e.currentTarget as HTMLElement).style.background = 'var(--bg-high)';
    }}
    onMouseOut={(e) => {
      if (active) return;
      (e.currentTarget as HTMLElement).style.background = 'transparent';
    }}
  >
    <div className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>
      {titleOf(title)}
    </div>
    <div className="flex items-center gap-2 mt-1">
      {tag && (
        <span
          className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded"
          style={{
            fontFamily: 'var(--font-display)',
            letterSpacing: '0.08em',
            background: 'var(--bg-high)',
            color: 'var(--text-muted)',
            border: '1px solid var(--border)',
          }}
        >
          {tag}
        </span>
      )}
      <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
        {formatDistanceToNow(new Date(updatedAt), { addSuffix: true })}
      </span>
    </div>
  </button>
);

// ─── Capability link picker ───────────────────────────────────────────────────

const CapabilityPicker: React.FC<{
  capabilities: Capability[];
  linkedIds: string[];
  onPick: (id: string) => void;
  onClose: () => void;
  isMobile?: boolean;
}> = ({ capabilities, linkedIds, onPick, onClose, isMobile }) => {
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [onClose]);

  const filtered = capabilities.filter((c) => {
    if (linkedIds.includes(c.id)) return false;
    if (!query) return true;
    return c.title.toLowerCase().includes(query.toLowerCase());
  });

  if (isMobile) {
    // Mobile: fixed bottom sheet with backdrop
    return (
      <>
        {/* Backdrop */}
        <div
          className="fixed inset-0 z-40"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={onClose}
        />
        {/* Sheet */}
        <div
          ref={ref}
          className="fixed left-0 right-0 bottom-0 z-50 rounded-t-xl overflow-hidden"
          style={{
            background: 'var(--bg-mid)',
            borderTop: '1px solid var(--border-mid)',
            boxShadow: '0 -8px 40px rgba(0,0,0,0.5)',
            maxHeight: '70vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            className="flex items-center justify-between px-4 py-3 flex-shrink-0"
            style={{ borderBottom: '1px solid var(--border)' }}
          >
            <span
              className="text-xs uppercase font-bold"
              style={{ fontFamily: 'var(--font-display)', letterSpacing: '0.12em', color: 'var(--text-muted)' }}
            >
              Link Capability
            </span>
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded"
              style={{ color: 'var(--text-muted)' }}
              aria-label="Close"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="px-3 py-2 flex-shrink-0" style={{ borderBottom: '1px solid var(--border)' }}>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search capabilities..."
              autoFocus
              className="w-full px-3 py-2 rounded outline-none"
              style={{
                background: 'var(--bg-deep)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                // font-size >= 16px to prevent iOS zoom
                fontSize: '1rem',
              }}
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-6 text-sm text-center" style={{ color: 'var(--text-muted)' }}>
                {query ? 'No matches' : capabilities.length === 0 ? 'No capabilities yet' : 'All linked'}
              </div>
            ) : (
              filtered.slice(0, 20).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => { onPick(c.id); setQuery(''); }}
                  className="w-full text-left px-4 py-3 transition-colors"
                  style={{ color: 'var(--text)', borderBottom: '1px solid var(--border)' }}
                  onTouchStart={(e) => {
                    (e.currentTarget as HTMLElement).style.background = 'var(--bg-high)';
                  }}
                  onTouchEnd={(e) => {
                    (e.currentTarget as HTMLElement).style.background = 'transparent';
                  }}
                >
                  <div className="text-sm font-medium truncate">{c.title}</div>
                  <div className="text-[11px] uppercase mt-0.5" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-display)', letterSpacing: '0.08em' }}>
                    {c.type}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      </>
    );
  }

  // Desktop: absolute dropdown
  return (
    <div
      ref={ref}
      className="absolute z-30 mt-1 w-72 rounded-lg shadow-xl overflow-hidden"
      style={{
        background: 'var(--bg-mid)',
        border: '1px solid var(--border-mid)',
        boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
      }}
    >
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search capabilities..."
        autoFocus
        className="w-full px-3 py-2 text-sm outline-none"
        style={{
          background: 'var(--bg-deep)',
          color: 'var(--text)',
          borderBottom: '1px solid var(--border)',
          fontSize: '1rem',
        }}
      />
      <div className="max-h-64 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="px-3 py-4 text-xs text-center" style={{ color: 'var(--text-muted)' }}>
            {query ? 'No matches' : capabilities.length === 0 ? 'No capabilities yet' : 'All linked'}
          </div>
        ) : (
          filtered.slice(0, 12).map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => {
                onPick(c.id);
                setQuery('');
              }}
              className="w-full text-left px-3 py-2 text-sm transition-colors"
              style={{ color: 'var(--text)' }}
              onMouseOver={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'var(--bg-high)';
              }}
              onMouseOut={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'transparent';
              }}
            >
              <div className="truncate">{c.title}</div>
              <div className="text-[10px] uppercase mt-0.5" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-display)', letterSpacing: '0.08em' }}>
                {c.type}
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
};

// ─── NotesPage ────────────────────────────────────────────────────────────────

const NotesPage: React.FC = () => {
  const history = useHistory();
  const activeWorkspace = useWorkspaceStore(selectActiveWorkspace);
  const workspaceId = activeWorkspace?.id ?? null;
  const isMobile = useUIStore((s) => s.isMobile);
  const addToast = useUIStore((s) => s.addToast);

  const notes = useNotesStore((s) => s.notes);
  const setNotes = useNotesStore((s) => s.setNotes);
  const activeNote = useNotesStore((s) => s.activeNote);
  const setActiveNote = useNotesStore((s) => s.setActiveNote);
  const upsertNoteListItem = useNotesStore((s) => s.upsertNoteListItem);
  const removeNoteListItem = useNotesStore((s) => s.removeNoteListItem);
  const updateActiveNote = useNotesStore((s) => s.updateActiveNote);

  const [searchQuery, setSearchQuery] = useState('');
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [tagInput, setTagInput] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [listVisibleOnMobile, setListVisibleOnMobile] = useState(true);

  const titleSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tagsSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const requestedNoteId = useQueryParam('note');

  // ─── Load notes list ────────────────────────────────────────────────────────
  const { isLoading: notesLoading } = useQuery({
    queryKey: ['notes', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];
      const data = await notesApi.list(workspaceId);
      setNotes(data);
      return data;
    },
    enabled: Boolean(workspaceId),
    staleTime: 30_000,
  });

  // ─── Load capabilities for linking ─────────────────────────────────────────
  const { data: capabilitiesData } = useQuery({
    queryKey: ['capabilities-for-notes', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];
      const res = await capabilitiesApi.list(workspaceId, { pageSize: 200 });
      return res.items;
    },
    enabled: Boolean(workspaceId),
    staleTime: 60_000,
  });
  const capabilities = capabilitiesData ?? [];

  // ─── Auto-select first note OR ?note= param ────────────────────────────────
  useEffect(() => {
    if (!workspaceId || notesLoading) return;

    const targetId =
      requestedNoteId && notes.some((n) => n.id === requestedNoteId)
        ? requestedNoteId
        : notes[0]?.id ?? null;

    if (targetId && activeNote?.id !== targetId) {
      notesApi
        .get(workspaceId, targetId)
        .then((note) => {
          setActiveNote(note);
          if (isMobile) setListVisibleOnMobile(false);
        })
        .catch((err) => {
          console.error('[NotesPage] load note failed', err);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, notes, requestedNoteId, notesLoading]);

  // ─── Save helpers ──────────────────────────────────────────────────────────
  const saveActiveNote = useCallback(
    async (patch: { title?: string; content?: string; tags?: string[] }) => {
      if (!workspaceId || !activeNote) return;
      setSaveState('saving');
      try {
        const updated = await notesApi.update(workspaceId, activeNote.id, patch);
        upsertNoteListItem({
          id: updated.id,
          workspaceId: updated.workspaceId,
          title: updated.title,
          tags: updated.tags,
          createdAt: updated.createdAt,
          updatedAt: updated.updatedAt,
        });
        updateActiveNote({
          title: updated.title,
          content: updated.content,
          tags: updated.tags,
          updatedAt: updated.updatedAt,
        });
        setSaveState('saved');
        // Fade to idle after a moment
        setTimeout(() => setSaveState((s) => (s === 'saved' ? 'idle' : s)), 1500);
      } catch (err) {
        console.error('[NotesPage] save failed', err);
        addToast({ title: 'Save failed', variant: 'error' });
        setSaveState('idle');
      }
    },
    [workspaceId, activeNote, upsertNoteListItem, updateActiveNote, addToast]
  );

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const handleCreate = useCallback(async () => {
    if (!workspaceId) {
      addToast({
        title: 'No workspace selected',
        description: 'Pick a workspace before creating a note.',
        variant: 'warning',
      });
      return;
    }
    try {
      const emptyDoc = JSON.stringify({ type: 'doc', content: [] });
      const note = await notesApi.create(workspaceId, {
        title: '',
        content: emptyDoc,
        tags: [],
      });
      upsertNoteListItem({
        id: note.id,
        workspaceId: note.workspaceId,
        title: note.title,
        tags: note.tags,
        createdAt: note.createdAt,
        updatedAt: note.updatedAt,
      });
      setActiveNote(note);
      setListVisibleOnMobile(false);
      history.replace(`/notes?note=${note.id}`);
    } catch (err) {
      console.error('[NotesPage] create failed', err);
      const message =
        err instanceof Error ? err.message : 'Unexpected error creating note.';
      addToast({
        title: 'Could not create note',
        description: message,
        variant: 'error',
      });
    }
  }, [workspaceId, upsertNoteListItem, setActiveNote, history, addToast]);

  const handleSelect = useCallback(
    async (id: string) => {
      if (!workspaceId) return;
      if (id === activeNote?.id) {
        if (isMobile) setListVisibleOnMobile(false);
        return;
      }
      try {
        const note = await notesApi.get(workspaceId, id);
        setActiveNote(note);
        history.replace(`/notes?note=${id}`);
        if (isMobile) setListVisibleOnMobile(false);
      } catch (err) {
        console.error('[NotesPage] select failed', err);
        addToast({ title: 'Could not open note', variant: 'error' });
      }
    },
    [workspaceId, activeNote, history, isMobile, addToast]
  );

  const handleTitleChange = (value: string) => {
    if (!activeNote) return;
    updateActiveNote({ title: value });
    setSaveState('dirty');
    if (titleSaveTimerRef.current) clearTimeout(titleSaveTimerRef.current);
    titleSaveTimerRef.current = setTimeout(() => {
      saveActiveNote({ title: value });
    }, 800);
  };

  const handleContentSave = useCallback(
    (nextContent: string) => {
      if (!activeNote) return;
      saveActiveNote({ content: nextContent });
    },
    [activeNote, saveActiveNote]
  );

  const commitTag = () => {
    if (!activeNote) return;
    const trimmed = tagInput.trim().replace(/,$/, '').trim();
    if (!trimmed) {
      setTagInput('');
      return;
    }
    if (activeNote.tags.includes(trimmed)) {
      setTagInput('');
      return;
    }
    const next = [...activeNote.tags, trimmed];
    updateActiveNote({ tags: next });
    setTagInput('');
    if (tagsSaveTimerRef.current) clearTimeout(tagsSaveTimerRef.current);
    saveActiveNote({ tags: next });
  };

  const removeTag = (tag: string) => {
    if (!activeNote) return;
    const next = activeNote.tags.filter((t) => t !== tag);
    updateActiveNote({ tags: next });
    saveActiveNote({ tags: next });
  };

  const handleLinkCapability = async (capabilityId: string) => {
    if (!workspaceId || !activeNote) return;
    try {
      const linked = await notesApi.linkCapability(workspaceId, activeNote.id, capabilityId);
      updateActiveNote({ linkedCapabilityIds: linked });
      setShowPicker(false);
    } catch (err) {
      console.error('[NotesPage] link failed', err);
      addToast({ title: 'Could not link capability', variant: 'error' });
    }
  };

  const handleUnlinkCapability = async (capabilityId: string) => {
    if (!workspaceId || !activeNote) return;
    try {
      await notesApi.unlinkCapability(workspaceId, activeNote.id, capabilityId);
      updateActiveNote({
        linkedCapabilityIds: activeNote.linkedCapabilityIds.filter((id) => id !== capabilityId),
      });
    } catch (err) {
      console.error('[NotesPage] unlink failed', err);
      addToast({ title: 'Could not unlink capability', variant: 'error' });
    }
  };

  const handleDelete = useCallback(async () => {
    if (!workspaceId || !activeNote) return;
    try {
      await notesApi.delete(workspaceId, activeNote.id);
      removeNoteListItem(activeNote.id);
      setConfirmDelete(false);
      addToast({ title: 'Note deleted', variant: 'success' });
      history.replace('/notes');
    } catch (err) {
      console.error('[NotesPage] delete failed', err);
      addToast({ title: 'Delete failed', variant: 'error' });
    }
  }, [workspaceId, activeNote, removeNoteListItem, addToast, history]);

  // ─── Filtered list ─────────────────────────────────────────────────────────
  const filteredNotes = useMemo(() => {
    if (!searchQuery.trim()) return notes;
    const q = searchQuery.toLowerCase();
    return notes.filter(
      (n) =>
        titleOf(n.title).toLowerCase().includes(q) ||
        n.tags.some((t) => t.toLowerCase().includes(q))
    );
  }, [notes, searchQuery]);

  // ─── Render ────────────────────────────────────────────────────────────────
  const showList = !isMobile || listVisibleOnMobile;
  const showEditor = !isMobile || !listVisibleOnMobile;

  return (
    <AppShell>
      <div className="flex h-full overflow-hidden" style={{ background: 'var(--bg-base)' }}>
            {/* ── Left panel ─────────────────────────────────────────────── */}
            {showList && (
              <aside
                className="flex flex-col flex-shrink-0"
                style={{
                  width: isMobile ? '100%' : '280px',
                  background: 'var(--bg-deep)',
                  borderRight: isMobile ? 'none' : '1px solid var(--border)',
                }}
              >
                <div className="px-3 py-3 flex-shrink-0" style={{ borderBottom: '1px solid var(--border)' }}>
                  <button
                    type="button"
                    onClick={handleCreate}
                    disabled={!workspaceId}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded text-xs font-bold uppercase transition-all"
                    style={{
                      fontFamily: 'var(--font-display)',
                      letterSpacing: '0.14em',
                      background: 'var(--crimson)',
                      color: '#fff',
                      boxShadow: '0 0 14px rgba(192,57,43,0.35)',
                      opacity: workspaceId ? 1 : 0.6,
                    }}
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                    New Note
                  </button>
                </div>

                <div className="px-3 py-2 flex-shrink-0" style={{ borderBottom: '1px solid var(--border)' }}>
                  <div className="relative">
                    <svg
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      <circle cx="11" cy="11" r="8" />
                      <path d="m21 21-4.35-4.35" />
                    </svg>
                    <input
                      type="search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search notes..."
                      className="w-full pl-8 pr-2 py-1.5 text-sm rounded outline-none"
                      style={{
                        background: 'var(--bg-mid)',
                        color: 'var(--text)',
                        border: '1px solid var(--border)',
                      }}
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                  {notesLoading ? (
                    <div className="p-3 space-y-2">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <div
                          key={i}
                          className="h-12 rounded animate-pulse"
                          style={{ background: 'var(--bg-high)' }}
                        />
                      ))}
                    </div>
                  ) : filteredNotes.length === 0 ? (
                    <div className="px-4 py-8 text-center">
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {searchQuery ? 'No matches' : 'No notes yet'}
                      </p>
                    </div>
                  ) : (
                    filteredNotes.map((n) => (
                      <NoteRow
                        key={n.id}
                        id={n.id}
                        title={n.title}
                        tag={n.tags[0]}
                        updatedAt={n.updatedAt}
                        active={n.id === activeNote?.id}
                        onClick={() => handleSelect(n.id)}
                      />
                    ))
                  )}
                </div>
              </aside>
            )}

            {/* ── Right panel ────────────────────────────────────────────── */}
            {showEditor && (
              <section className="flex-1 flex flex-col min-w-0 overflow-hidden" style={{ overflowX: 'hidden' }}>
                {!activeNote ? (
                  notes.length === 0 && !notesLoading ? (
                    <div className="flex-1 flex items-center justify-center p-6">
                      <div className="card p-8 max-w-sm w-full text-center">
                        <p className="eyebrow mb-3 justify-center">Notes</p>
                        <h2
                          className="text-xl font-bold mb-3"
                          style={{
                            fontFamily: 'var(--font-display)',
                            color: 'var(--text)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                          }}
                        >
                          No notes yet
                        </h2>
                        <p className="text-sm mb-5" style={{ color: 'var(--text-muted)' }}>
                          Capture an idea, link it to a capability, and grow your graph.
                        </p>
                        <button
                          type="button"
                          onClick={handleCreate}
                          className="btn-primary mx-auto"
                          disabled={!workspaceId}
                        >
                          Create your first note
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex-1" />
                  )
                ) : (
                  <>
                    {/* Header */}
                    <div
                      className={`flex-shrink-0 ${isMobile ? 'px-3 pt-3 pb-2' : 'px-6 pt-5 pb-3'} flex items-start gap-2`}
                      style={{ borderBottom: '1px solid var(--border)' }}
                    >
                      {isMobile && (
                        <button
                          type="button"
                          onClick={() => setListVisibleOnMobile(true)}
                          className="mt-1 p-1.5 rounded transition-colors"
                          style={{ color: 'var(--text-muted)' }}
                          aria-label="Back to list"
                        >
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="m15 18-6-6 6-6" />
                          </svg>
                        </button>
                      )}
                      <div className="flex-1 min-w-0">
                        <input
                          type="text"
                          value={activeNote.title}
                          onChange={(e) => handleTitleChange(e.target.value)}
                          placeholder="Untitled"
                          className="w-full bg-transparent outline-none font-bold"
                          style={{
                            fontFamily: 'var(--font-display)',
                            color: 'var(--text)',
                            fontSize: isMobile ? '1.25rem' : '1.75rem',
                            letterSpacing: '0.01em',
                            textTransform: 'uppercase',
                            border: 'none',
                            padding: 0,
                          }}
                        />
                        {/* Tags row */}
                        <div className="flex items-center flex-wrap gap-1.5 mt-3">
                          {activeNote.tags.map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px]"
                              style={{
                                background: 'var(--bg-high)',
                                color: 'var(--text-dim)',
                                border: '1px solid var(--border)',
                              }}
                            >
                              {tag}
                              <button
                                type="button"
                                onClick={() => removeTag(tag)}
                                className="opacity-50 hover:opacity-100"
                                aria-label={`Remove tag ${tag}`}
                              >
                                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M18 6 6 18M6 6l12 12" />
                                </svg>
                              </button>
                            </span>
                          ))}
                          <input
                            type="text"
                            value={tagInput}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v.endsWith(',')) {
                                setTagInput(v);
                                setTimeout(() => commitTag(), 0);
                              } else {
                                setTagInput(v);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                commitTag();
                              } else if (
                                e.key === 'Backspace' &&
                                tagInput === '' &&
                                activeNote.tags.length > 0
                              ) {
                                removeTag(activeNote.tags[activeNote.tags.length - 1]);
                              }
                            }}
                            onBlur={commitTag}
                            placeholder={activeNote.tags.length === 0 ? 'Add tags...' : ''}
                            className="bg-transparent outline-none flex-1 min-w-[80px]"
                            style={{
                              color: 'var(--text)',
                              padding: '2px 0',
                              // Must be >= 16px to prevent iOS Safari auto-zoom on focus
                              fontSize: '1rem',
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                        {saveState !== 'idle' && (
                          <span
                            className="text-[10px] uppercase font-bold"
                            style={{
                              fontFamily: 'var(--font-display)',
                              letterSpacing: '0.14em',
                              color:
                                saveState === 'saving'
                                  ? 'var(--crimson-light)'
                                  : 'var(--text-dim)',
                            }}
                          >
                            {saveState === 'saving' ? 'Saving…' : 'Saved'}
                          </span>
                        )}
                        {!confirmDelete ? (
                          <button
                            type="button"
                            onClick={() => setConfirmDelete(true)}
                            className="text-[10px] uppercase font-bold transition-colors px-1 py-0.5 rounded"
                            style={{
                              fontFamily: 'var(--font-display)',
                              letterSpacing: '0.12em',
                              color: 'var(--text-muted)',
                              border: '1px solid var(--border)',
                            }}
                            onMouseOver={(e) => {
                              (e.currentTarget as HTMLElement).style.color = 'var(--crimson-light)';
                              (e.currentTarget as HTMLElement).style.borderColor = 'var(--crimson-border)';
                            }}
                            onMouseOut={(e) => {
                              (e.currentTarget as HTMLElement).style.color = 'var(--text-muted)';
                              (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)';
                            }}
                          >
                            Delete
                          </button>
                        ) : (
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={handleDelete}
                              className="text-[10px] uppercase font-bold px-2 py-1 rounded"
                              style={{
                                fontFamily: 'var(--font-display)',
                                letterSpacing: '0.12em',
                                background: 'var(--crimson)',
                                color: '#fff',
                              }}
                            >
                              Confirm
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmDelete(false)}
                              className="text-[10px] uppercase font-bold px-2 py-1 rounded"
                              style={{
                                fontFamily: 'var(--font-display)',
                                letterSpacing: '0.12em',
                                color: 'var(--text-muted)',
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Linked capabilities */}
                    <div
                      className={`flex-shrink-0 ${isMobile ? 'px-3' : 'px-6'} py-3 flex items-center gap-2 flex-wrap relative`}
                      style={{ borderBottom: '1px solid var(--border)' }}
                    >
                      <span
                        className="text-[10px] uppercase font-bold mr-1"
                        style={{
                          fontFamily: 'var(--font-display)',
                          letterSpacing: '0.14em',
                          color: 'var(--text-muted)',
                        }}
                      >
                        Linked
                      </span>
                      {activeNote.linkedCapabilityIds.map((id) => {
                        const cap = capabilities.find((c) => c.id === id);
                        const label = cap?.title ?? id.slice(0, 6);
                        return (
                          <span
                            key={id}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px]"
                            style={{
                              background: 'var(--crimson-dim)',
                              color: 'var(--crimson-light)',
                              border: '1px solid var(--crimson-border)',
                            }}
                          >
                            <button
                              type="button"
                              onClick={() => cap && history.push(`/capabilities/${cap.id}`)}
                              className="truncate max-w-[180px]"
                              style={{ color: 'var(--crimson-light)' }}
                            >
                              {label}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleUnlinkCapability(id)}
                              className="opacity-60 hover:opacity-100"
                              aria-label="Unlink"
                            >
                              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 6 6 18M6 6l12 12" />
                              </svg>
                            </button>
                          </span>
                        );
                      })}
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowPicker((v) => !v)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] uppercase font-bold transition-colors"
                          style={{
                            fontFamily: 'var(--font-display)',
                            letterSpacing: '0.1em',
                            background: 'transparent',
                            color: 'var(--text-muted)',
                            border: '1px dashed var(--border-mid)',
                          }}
                          onMouseOver={(e) => {
                            (e.currentTarget as HTMLElement).style.color = 'var(--text)';
                            (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-strong)';
                          }}
                          onMouseOut={(e) => {
                            (e.currentTarget as HTMLElement).style.color = 'var(--text-muted)';
                            (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-mid)';
                          }}
                        >
                          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M12 5v14M5 12h14" />
                          </svg>
                          Link capability
                        </button>
                        {showPicker && (
                          <CapabilityPicker
                            capabilities={capabilities}
                            linkedIds={activeNote.linkedCapabilityIds}
                            onPick={handleLinkCapability}
                            onClose={() => setShowPicker(false)}
                            isMobile={isMobile}
                          />
                        )}
                      </div>
                    </div>

                    {/* Editor */}
                    <div className="flex-1 min-h-0 overflow-hidden">
                      <NoteEditor
                        key={activeNote.id}
                        noteId={activeNote.id}
                        content={activeNote.content}
                        onSave={handleContentSave}
                      />
                    </div>
                  </>
                )}
              </section>
            )}
      </div>
    </AppShell>
  );
};

export default NotesPage;
